import { HeroBannerSlide as HeroBannerSlideType, SanityImage } from '@workspace/interfaces';
import { cn } from '@workspace/lib';
import { Picture, type PictureProps } from '@workspace/ui/common';
import { HeroBannerVideo } from './HeroBannerVideo';
import { HeroBannerLayout, HeroBannerSize } from '../hero-banner.types';
import { getHeroBannerMinHeightClass } from '../hero-banner-util';

type HeroBannerImage = HeroBannerSlideType['image'] | SanityImage;

interface HeroBannerBackgroundProps {
  blockImage?: HeroBannerImage;
  contentPosition: 'left' | 'center' | 'right';
  isVideo?: boolean | null;
  videoUrl?: string | null;
  videoFile?: SanityImage | null;
  bynderVideo?: any;
  poster?: SanityImage | null;
  isActive?: boolean | null;
  size?: HeroBannerSize;
  shouldPreload?: boolean;
  showGradient?: boolean | null;
  focalPoint?: { x?: number; y?: number };
  layout?: HeroBannerLayout;
}

/**
 * Bynder crop per hero variant. A crop taller than the source makes Bynder return the uncropped original, so the
 * short slim banner uses a wider desktop crop that fits common 16:9 sources (1920x1080).
 */
const HERO_PICTURE_PROPS = {
  slim: {
    desktopWidth: 1920,
    desktopHeight: 692,
    tabletWidth: 1000,
    tabletHeight: 600,
    mobileWidth: 480,
    mobileHeight: 632,
    sizes: '100vw',
  },
  medium: {
    desktopWidth: 1920,
    desktopHeight: 1107,
    tabletWidth: 1000,
    tabletHeight: 600,
    mobileWidth: 480,
    mobileHeight: 632,
    sizes: '100vw',
  },
  full: {
    desktopWidth: 1920,
    desktopHeight: 1107,
    tabletWidth: 1000,
    tabletHeight: 600,
    mobileWidth: 480,
    mobileHeight: 632,
    sizes: '100vw',
  },
  article: { desktopWidth: 630, desktopHeight: 420, mobileWidth: 326, mobileHeight: 217 },
} satisfies Record<HeroBannerSize | 'article', Partial<PictureProps>>;

const getGradientClass = (contentPosition: 'left' | 'center' | 'right'): string => {
  switch (contentPosition) {
    case 'center':
      return 'hero-gradient-center';
    case 'right':
      return 'hero-gradient-right';
    default:
      return 'hero-gradient-left';
  }
};

export function HeroBannerBackground({
  blockImage,
  contentPosition,
  isVideo = false,
  videoUrl,
  videoFile,
  bynderVideo,
  poster,
  isActive = false,
  shouldPreload = false,
  size = 'medium',
  showGradient = true,
  focalPoint: { x = 50, y = 50 } = {},
  layout = 'overlay',
}: HeroBannerBackgroundProps) {
  const gradientClass = getGradientClass(contentPosition);
  const minHeightClass = getHeroBannerMinHeightClass(size);
  const shouldPrioritize = shouldPreload || (isActive ?? false);
  const backgroundClass =
    layout === 'article' ? 'relative inset-auto size-full min-h-0' : cn('absolute inset-0', minHeightClass);

  // Render video background if isVideo is true and a video source exists
  if (isVideo && (videoUrl || videoFile || bynderVideo)) {
    return (
      <div className={backgroundClass} suppressHydrationWarning>
        <HeroBannerVideo
          videoUrl={videoUrl ?? ''}
          videoFile={videoFile ?? null}
          bynderVideo={bynderVideo ?? null}
          poster={poster ?? null}
          isActive={isActive}
          shouldPreload={shouldPrioritize}
        />
        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render image background if blockImage exists
  if (blockImage?.url) {
    return (
      <div className={backgroundClass} suppressHydrationWarning>
        <Picture
          image={blockImage}
          {...HERO_PICTURE_PROPS[layout === 'article' ? 'article' : size]}
          fill
          objectPosition={`${x}% ${y}%`}
          priority={shouldPrioritize}
        />

        {showGradient !== false && (
          <div className={cn('absolute inset-0 pointer-events-none hero-gradient', gradientClass)} />
        )}
      </div>
    );
  }

  // Render gradient only if no image or video
  return (
    <div className={cn('pointer-events-none hero-gradient', backgroundClass, gradientClass)} suppressHydrationWarning />
  );
}
